import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeftOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import { TrendingUp } from "lucide-react";
import authService from "../apiServices/authService";
import { toast } from "../common/Popover";

interface ResetFormData {
    email: string;
    newPassword: string;
    confirmPassword: string;
}

interface ResetErrors {
    // email?: string;
    newPassword?: string;
    confirmPassword?: string;
    ref?: string;
}

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const resetRef = useMemo(() => searchParams.get("ref") || "", [searchParams]);
    const [formData, setFormData] = useState<ResetFormData>({
        email: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [errors, setErrors] = useState<ResetErrors>({});
    const [loading, setLoading] = useState(false);

    const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,20}$/;


    const validateForm = () => {
        const newErrors: ResetErrors = {};

        if (!resetRef) {
            newErrors.ref = "Reset link is invalid or incomplete";
        }

        // if (!formData.email.trim()) {
        //     newErrors.email = "Email is required";
        // } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        //     newErrors.email = "Please enter a valid email";
        // }

        if (!formData.newPassword) {
            newErrors.newPassword = "New password is required";
        } else if (formData.newPassword.length < 8 && !passwordRegex.test(formData.newPassword)) {
            newErrors.newPassword =
                "Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character";
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = "Please confirm your new password";
        } else if (formData.confirmPassword !== formData.newPassword) {
            newErrors.confirmPassword = "Confirm password does not match";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (!resetRef) navigate("/user-login", { replace: true });
    }, [resetRef])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (errors[name as keyof ResetErrors]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const clearPasswords = () => {
        setFormData((prev) => ({
            ...prev,
            newPassword: "",
            confirmPassword: "",
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await authService.resetPassword({
                ref: resetRef,
                // email: formData.email.trim(),
                newPassword: formData.newPassword,
                confirmPassword: formData.confirmPassword,
            });

            if (response.meta.status) {
                toast("success", response.meta.message || "Password reset successfully", 3);
                navigate("/user-login", { replace: true });
            } else {
                clearPasswords();
                toast("error", response.meta.message || "Failed to reset password", 3);
            }
        } catch (apiError: any) {
            clearPasswords();
            toast("error", apiError.message || "Failed to reset password", 3);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            <div className="hidden md:flex md:w-1/2 bg-indigo-900 text-white flex-col justify-center px-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1F344C] to-[#1F344C] opacity-90"></div>
                <div className="relative z-10">
                    <h1 className="flex items-center justify-center gap-3 text-5xl font-bold mb-4 pr-14">
                        <TrendingUp className="text-5xl" size={40} />
                        <span>AffluenceAI</span>
                    </h1>
                    <h2 className="text-3xl font-light mb-6 text-center">Elevate Conversions</h2>
                    <p className="text-xl text-indigo-200 text-center">From affluent insights to instant chats</p>
                </div>
                <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 opacity-10 rounded-full translate-x-1/3 translate-y-1/3"></div>
            </div>

            <div className="w-full md:w-1/2 bg-white flex flex-col justify-center items-center p-8 md:p-12 relative">
                <div className="absolute top-6 w-full text-center m-auto mt-6">
                    <img src="https://tml-ad.s3.ap-south-1.amazonaws.com/ac526d81-fe38-11ee-a871-0a71828867bb/timesEngage_crosshair_1788356558106.png" alt="LOGO" className="w-48 m-auto" />
                    {/* <p className="text-xs text-gray-400">
                        TimesEngage © 2026, All rights reserved.
                    </p> */}
                </div>
                <div className="w-full max-w-md space-y-8 shadow-xl rounded-lg p-6">
                    <Link to="/user-login" className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        <ArrowLeftOutlined />
                        Back to login
                    </Link>

                    <div className="text-left">
                        <h2 className="text-3xl font-bold text-gray-900">Reset password</h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Enter your new password.
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        {errors.ref && (
                            <p className="text-sm text-red-600">{errors.ref}</p>
                        )}

                        <div className="space-y-5">
                            {/* <div>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={`appearance-none block w-full pl-10 px-3 py-3 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors`}
                                        placeholder="Enter email"
                                    />
                                </div>
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                )}
                            </div> */}

                            <div>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="newPassword"
                                        name="newPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        value={formData.newPassword}
                                        onChange={handleChange}
                                        className={`appearance-none block w-full pl-10 px-3 py-3 border ${errors.newPassword ? 'border-red-300' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors`}
                                        placeholder="Enter new password"
                                    />
                                </div>
                                {errors.newPassword && (
                                    <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                                )}
                            </div>

                            <div>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className={`appearance-none block w-full pl-10 px-3 py-3 border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors`}
                                        placeholder="Confirm new password"
                                    />
                                </div>
                                {errors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                                )}
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${loading ? 'bg-[#1F344C] cursor-not-allowed' : 'bg-[#1F344C] hover:bg-[#1F344C] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'} transition-colors`}
                        >
                            {loading ? "Resetting..." : "Reset password"}
                        </button>
                    </form>
                </div>

                <div className="absolute bottom-6 w-full text-center m-auto">
                    {/* <img src="https://tml-ad.s3.ap-south-1.amazonaws.com/ac526d81-fe38-11ee-a871-0a71828867bb/timesEngage_crosshair_1788356558106.png" alt="LOGO" className="w-24 m-auto" /> */}
                    <p className="text-xs text-gray-400">
                        Times Engage © 2026, All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
